import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook to get translation with styles from database
 * Usage: const { text, styles } = useStyledTranslation('hero.confidence');
 */
export const useStyledTranslation = (key) => {
  const { t, i18n } = useTranslation();
  const [styles, setStyles] = useState(null);
  
  // Function to load styles
  const loadStyles = () => {
    const lang = i18n.language || 'sv';
    
    try {
      // Access the translation resources
      const resources = i18n.store?.data?.[lang]?.translation;
      
      if (resources) {
        // Look for style key
        const styleKey = `_styles_${key}`;
        const savedStyles = resources[styleKey];
        
        if (savedStyles) {
          console.log(`[StyledText] Found styles for ${key}:`, savedStyles);
          setStyles(savedStyles);
        } else {
          console.log(`[StyledText] No styles found for ${key}`);
          setStyles(null);
        }
      } else {
        console.log(`[StyledText] Resources not loaded yet for ${lang}`);
      }
    } catch (error) {
      console.error(`[StyledText] Error loading styles for ${key}:`, error);
    }
  };
  
  useEffect(() => {
    // Load styles immediately
    loadStyles();
    
    // Also listen for language change events
    const handleLanguageChanged = () => {
      console.log(`[StyledText] Language changed, reloading styles for ${key}`);
      loadStyles();
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [key, i18n.language]);
  
  // Also try to load on mount after a short delay (for initial load)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadStyles();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  return {
    text: t(key),
    styles
  };
};

/**
 * Component to render styled translation
 * Usage: <StyledText translationKey="hero.confidence" />
 */
export const StyledText = ({ translationKey, className = '' }) => {
  const { text, styles } = useStyledTranslation(translationKey);
  
  // If styles exist, apply them
  const inlineStyle = styles ? {
    color: styles.color || '#000000',
    fontSize: `${styles.fontSize || 16}px`
  } : {};
  
  return (
    <span 
      className={className}
      style={inlineStyle}
    >
      {text}
    </span>
  );
};
